import {
  Box,
  DataTable,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridSortModel,
  gridClasses,
  MenuItem,
  Stack,
} from '@chronosphereio/chrono-ui';
import { Select } from '@mui/material';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { getNoDataMessage, useChartsTheme, useTimeZone } from '@/components';
import { TransformKind } from '@/core';
import { mergeTimeSeries, useDataQueryResult } from '@/plugin-system';
import { NoData } from '@/plugin-system/components/NoData';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { TablePanelConfigProvider } from './TableConfigProvider';
import { TablePanelLoaded } from './TablePanelLoaded';
import {
  DEFAULT_TABLE_HEIGHT,
  DEFAULT_TRANSFORM,
  sortColumns,
  TableProps,
  transformToColumnVisibilityModel,
  transformToGridSortModel,
  useTransformedTimeSeries,
} from './table-model';
import { useColumnStyles, useFormattedColumns } from './useFormattedColumns';
import { LINKS_COLUMN_FIELD_IDENTIFIER, useLinksColumn } from './useLinksColumn';
import {
  handleTableColumnOrderChange,
  handleTableColWidthChange,
  handleTableSortChange,
  isTableV2,
  useTransformedColumns,
} from './utils';

export function TablePanel(props: TableProps) {
  if (isTableV2(props.spec)) {
    return (
      <TablePanelConfigProvider onAddFilters={props.onAddFilters}>
        <TablePanelLoaded {...props} />
      </TablePanelConfigProvider>
    );
  }

  return <DeprecatedTablePanel {...props} />;
}

/**
 * Initial version of the table.
 * It is replaced by a newer version that uses data frames.
 * Do not remove, as this version may still be in use in production.
 * @deprecated
 */
export function DeprecatedTablePanel(props: TableProps) {
  const { spec, width, height, onPluginSpecChange, links } = props;
  const { transform: transform = DEFAULT_TRANSFORM, unit, sort, columnOverrides, thresholds } = spec;

  const contentDimensions = width && height ? { width, height } : undefined;

  const chartsTheme = useChartsTheme();
  const ctx = useDataQueryResult({ dataTypeFilter: 'TimeSeries' });
  const data = useMemo(() => {
    return mergeTimeSeries(ctx.queryResults);
  }, [ctx.queryResults]);

  const [selectedSeriesIdx, setSelectedSeriesIdx] = useState<number>(0);

  const hasNoSeries = data.length === 0;

  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();
  const { rows, columns } = useTransformedTimeSeries(data, transform, selectedSeriesIdx, {
    unit,
    timeZone,
    timeFormat,
  });

  const transformedColumns = useTransformedColumns({ columns, columnOverrides });

  const columnVisibilityModel = useMemo(() => {
    return transformToColumnVisibilityModel(columnOverrides);
  }, [columnOverrides]);

  const [queryResults, setQueryResults] = useState(ctx.queryResults);

  const handleSortChange = (newSort: GridSortModel) => {
    if (onPluginSpecChange) {
      handleTableSortChange({ newSort, spec, onPluginSpecChange });
    }
  };

  const handleColWidthChange = (params: GridColumnResizeParams) => {
    if (onPluginSpecChange) {
      handleTableColWidthChange({ params, spec, onPluginSpecChange });
    }
  };

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    if (onPluginSpecChange) {
      handleTableColumnOrderChange({ params, columns, spec, onPluginSpecChange });
    }
  };

  // Reset selected series when data changes
  useEffect(() => {
    if (!isEqual(queryResults, ctx.queryResults)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSeriesIdx(0);
      setQueryResults(ctx.queryResults);
    }
  }, [ctx.queryResults, queryResults, setQueryResults]);

  const sortedColumns = useMemo(
    () => sortColumns(transformedColumns, spec.column_order),
    [transformedColumns, spec.column_order]
  );

  const formattedColumns = useFormattedColumns(sortedColumns, columnOverrides, thresholds);
  const columnStyles = useColumnStyles(columnOverrides, thresholds);
  const columnsWithLinks = useLinksColumn(links, formattedColumns);

  if (contentDimensions === undefined) {
    return null;
  }

  const noDataMessage = getNoDataMessage(chartsTheme);

  return (
    <>
      {hasNoSeries ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={contentDimensions.width}
          height={contentDimensions?.height ?? DEFAULT_TABLE_HEIGHT}
        >
          <NoData />
        </Box>
      ) : (
        <Stack direction="column" spacing={1} height={contentDimensions?.height} width={contentDimensions?.width}>
          {transform.kind === TransformKind.Raw && (
            <Box padding={1} paddingBottom={0}>
              <Select
                placeholder="Select a series"
                fullWidth
                value={selectedSeriesIdx}
                onChange={(e) => setSelectedSeriesIdx(e.target.value as number)}
              >
                {data.map((s, idx) => (
                  <MenuItem key={s.name} value={idx}>
                    {`${s.name || s.formattedName}`}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          <DataTable
            enableExpandableCells
            sx={{
              // Wrap in the row to ensure the css specificity is sufficient to "win" over the
              // default styles when using expandable cells.
              [`& .${gridClasses.row}`]: {
                ...columnStyles,
              },
            }}
            getRowId={(row) => row._vfId}
            loading={ctx.isLoading}
            pagination={false}
            columns={columnsWithLinks}
            rows={rows}
            hideFooterRowCount
            hideFooter
            localeText={{
              noRowsLabel: noDataMessage,
            }}
            pinnedColumns={{
              right: [LINKS_COLUMN_FIELD_IDENTIFIER],
            }}
            sortModel={sort && transformToGridSortModel(sort)}
            onSortModelChange={onPluginSpecChange && handleSortChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnWidthChange={handleColWidthChange}
            onColumnOrderChange={handleColumnOrderChange}
          />
        </Stack>
      )}
    </>
  );
}
